import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { clientAPI } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import {
  ShoppingBagIcon,
  HeartIcon,
  MapPinIcon,
  MagnifyingGlassIcon,
  BuildingStorefrontIcon,
  ClockIcon,
  ChartBarIcon,
  ArrowRightIcon,
  Cog6ToothIcon,
  WalletIcon,
  TruckIcon,
  ChatBubbleLeftRightIcon,
  PhoneIcon,
  EnvelopeIcon
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolidIcon } from '@heroicons/react/24/solid';

const ORDER_STATUSES = [
  { value: '', label: 'All Orders' },
  { value: 'pending', label: 'Pending' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'processing', label: 'Processing' },
  { value: 'shipped', label: 'Shipped' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'cancelled', label: 'Cancelled' }
];

const ClientDashboard = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [recentOrders, setRecentOrders] = useState([]);
  const [allOrders, setAllOrders] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [chartStatusFilter, setChartStatusFilter] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [statsRes, ordersRes, allOrdersRes, favsRes] = await Promise.all([
        clientAPI.getStats(),
        clientAPI.listOrders({ limit: 5 }),
        clientAPI.listOrders({}),
        clientAPI.getFavorites()
      ]);
      setStats(statsRes.data);
      setRecentOrders(ordersRes.data.orders || []);
      setAllOrders(allOrdersRes.data.orders || []);
      setFavorites(favsRes.data.favorites || []);
    } catch (error) {
      console.error('Load client data error:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredOrders = useMemo(() => {
    if (!chartStatusFilter) return allOrders;
    return allOrders.filter(order => order.status === chartStatusFilter);
  }, [allOrders, chartStatusFilter]);

  const chartData = useMemo(() => {
    if (!allOrders.length) return [];

    const now = new Date();
    const twelveMonthsAgo = new Date(now);
    twelveMonthsAgo.setMonth(twelveMonthsAgo.getMonth() - 12);

    const weeks = [];
    let currentWeek = new Date(twelveMonthsAgo);
    currentWeek.setDate(currentWeek.getDate() - currentWeek.getDay());

    while (currentWeek <= now) {
      weeks.push(new Date(currentWeek));
      currentWeek.setDate(currentWeek.getDate() + 7);
    }

    const weeklyData = weeks.map((weekStart) => {
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 6);
      weekEnd.setHours(23, 59, 59, 999);

      const weekOrders = filteredOrders.filter(order => {
        const orderDate = new Date(order.createdAt);
        return orderDate >= weekStart && orderDate <= weekEnd;
      });

      const totalSpent = weekOrders.reduce((sum, order) => {
        if (order.status !== 'cancelled') {
          return sum + Number(order.totalAmount || 0);
        }
        return sum;
      }, 0);

      const weekLabel = weekStart.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric' 
      });

      return {
        week: weekLabel,
        orders: weekOrders.length,
        spent: Math.round(totalSpent)
      };
    });

    return weeklyData.map((d, i) => ({
      ...d,
      weekDisplay: i % 4 === 0 ? d.week : ''
    }));
  }, [allOrders, filteredOrders]);

  const chartSummary = useMemo(() => {
    if (!chartData.length) return { totalOrders: 0, totalSpent: 0 };
    return {
      totalOrders: chartData.reduce((sum, d) => sum + d.orders, 0),
      totalSpent: chartData.reduce((sum, d) => sum + d.spent, 0)
    };
  }, [chartData]);

  const getStatusColor = (status) => {
    switch(status) {
      case 'delivered': return 'bg-green-100 text-green-700';
      case 'shipped': return 'bg-blue-100 text-blue-700';
      case 'confirmed': case 'processing': return 'bg-amber-100 text-amber-700';
      case 'cancelled': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <div className="relative overflow-hidden bg-gradient-to-br from-amber-500 via-orange-500 to-red-500 rounded-2xl p-6 sm:p-8 text-white">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-40 h-40 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2"></div>
        
        <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <p className="text-amber-100 text-sm mb-1">Welcome back,</p>
            <h1 className="text-2xl sm:text-3xl font-bold">
              {user?.firstName || 'Customer'}!
            </h1>
            <p className="text-amber-100 mt-2 text-sm sm:text-base">
              Find fresh eggs, chicken, and more from local farms
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link
              to="/client/explore"
              className="inline-flex items-center px-4 py-2 bg-white/20 backdrop-blur text-white rounded-lg hover:bg-white/30 transition-colors text-sm font-medium"
            >
              <MagnifyingGlassIcon className="h-4 w-4 mr-2" />
              Explore Farms
            </Link>
            <Link
              to="/client/orders"
              className="inline-flex items-center px-4 py-2 bg-white text-amber-600 rounded-lg hover:bg-amber-50 transition-colors text-sm font-medium"
            >
              <ShoppingBagIcon className="h-4 w-4 mr-2" />
              My Orders
            </Link>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl p-5 text-white shadow-lg shadow-amber-500/20">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-amber-100 text-sm">Orders</p>
              <p className="text-3xl font-bold mt-1">{stats?.totalOrders || 0}</p>
              <p className="text-amber-200 text-xs mt-2">
                Total placed
              </p>
            </div>
            <div className="p-2 bg-white/20 rounded-lg">
              <ShoppingBagIcon className="h-6 w-6" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl p-5 text-white shadow-lg shadow-green-500/20">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-green-100 text-sm">Spent</p>
              <p className="text-2xl sm:text-3xl font-bold mt-1">
                KES {(stats?.totalSpent || 0).toLocaleString()}
              </p>
              <p className="text-green-200 text-xs mt-2">
                All time
              </p>
            </div>
            <div className="p-2 bg-white/20 rounded-lg">
              <WalletIcon className="h-6 w-6" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-rose-500 to-pink-600 rounded-xl p-5 text-white shadow-lg shadow-rose-500/20">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-rose-100 text-sm">Favorites</p>
              <p className="text-3xl font-bold mt-1">{stats?.favoriteFarmsCount || favorites.length || 0}</p>
              <p className="text-rose-200 text-xs mt-2">
                Saved farms
              </p>
            </div>
            <div className="p-2 bg-white/20 rounded-lg">
              <HeartSolidIcon className="h-6 w-6" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl p-5 text-white shadow-lg shadow-blue-500/20">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-blue-100 text-sm">Pending</p>
              <p className="text-3xl font-bold mt-1">
                {allOrders.filter(o => ['pending', 'confirmed', 'processing', 'shipped'].includes(o.status)).length}
              </p>
              <p className="text-blue-200 text-xs mt-2">
                Active orders
              </p>
            </div>
            <div className="p-2 bg-white/20 rounded-lg">
              <TruckIcon className="h-6 w-6" />
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Link to="/client/explore" className="bg-white rounded-xl p-4 border border-gray-100 hover:shadow-md hover:border-amber-200 transition-all group">
          <div className="p-2 bg-amber-50 rounded-lg w-fit group-hover:bg-amber-100 transition-colors">
            <MagnifyingGlassIcon className="h-5 w-5 text-amber-600" />
          </div>
          <p className="font-medium text-gray-900 mt-3">Explore</p>
          <p className="text-xs text-gray-500">Find farms</p>
        </Link>
        <Link to="/client/orders" className="bg-white rounded-xl p-4 border border-gray-100 hover:shadow-md hover:border-green-200 transition-all group">
          <div className="p-2 bg-green-50 rounded-lg w-fit group-hover:bg-green-100 transition-colors">
            <ShoppingBagIcon className="h-5 w-5 text-green-600" />
          </div>
          <p className="font-medium text-gray-900 mt-3">Orders</p>
          <p className="text-xs text-gray-500">Track purchases</p>
        </Link>
        <Link to="/wallet" className="bg-white rounded-xl p-4 border border-gray-100 hover:shadow-md hover:border-blue-200 transition-all group">
          <div className="p-2 bg-blue-50 rounded-lg w-fit group-hover:bg-blue-100 transition-colors">
            <WalletIcon className="h-5 w-5 text-blue-600" />
          </div>
          <p className="font-medium text-gray-900 mt-3">Wallet</p>
          <p className="text-xs text-gray-500">Manage funds</p>
        </Link>
        <Link to="/client/settings" className="bg-white rounded-xl p-4 border border-gray-100 hover:shadow-md hover:border-gray-300 transition-all group">
          <div className="p-2 bg-gray-100 rounded-lg w-fit group-hover:bg-gray-200 transition-colors">
            <Cog6ToothIcon className="h-5 w-5 text-gray-600" />
          </div>
          <p className="font-medium text-gray-900 mt-3">Settings</p>
          <p className="text-xs text-gray-500">Preferences</p>
        </Link>
      </div>

      {/* Chart Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-6 border-b border-gray-100">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h2 className="text-lg font-bold text-gray-900 flex items-center">
                <ChartBarIcon className="h-5 w-5 mr-2 text-gray-400" />
                Orders & Spending Trend
              </h2>
              <p className="text-sm text-gray-500 mt-1">Your purchase history</p>
            </div>
            <div className="flex items-center gap-4">
              <select
                value={chartStatusFilter}
                onChange={(e) => setChartStatusFilter(e.target.value)}
                className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              >
                {ORDER_STATUSES.map(s => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="flex gap-6 mt-4">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-amber-500"></div>
              <span className="text-sm text-gray-600">
                Orders: <span className="font-semibold text-gray-900">{chartSummary.totalOrders}</span>
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-green-500"></div>
              <span className="text-sm text-gray-600">
                Spent: <span className="font-semibold text-gray-900">KES {chartSummary.totalSpent.toLocaleString()}</span>
              </span>
            </div>
          </div>
        </div>
        
        <div className="p-6">
          {chartData.length > 0 ? (
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="colorSpent" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorClientOrders" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#f59e0b" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis 
                    dataKey="weekDisplay" 
                    tick={{ fontSize: 11, fill: '#6b7280' }} 
                    axisLine={{ stroke: '#e5e7eb' }}
                  />
                  <YAxis 
                    yAxisId="left" 
                    tick={{ fontSize: 11, fill: '#6b7280' }} 
                    tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}K` : v}
                    axisLine={{ stroke: '#e5e7eb' }}
                  />
                  <YAxis 
                    yAxisId="right" 
                    orientation="right" 
                    tick={{ fontSize: 11, fill: '#6b7280' }}
                    axisLine={{ stroke: '#e5e7eb' }}
                  />
                  <Tooltip 
                    contentStyle={{ borderRadius: '8px', border: '1px solid #e5e7eb' }}
                    formatter={(value, name) => [
                      name === 'spent' ? `KES ${value.toLocaleString()}` : value,
                      name === 'spent' ? 'Spent' : 'Orders'
                    ]}
                  />
                  <Area 
                    yAxisId="left" 
                    type="monotone" 
                    dataKey="spent" 
                    stroke="#10b981" 
                    strokeWidth={2}
                    fill="url(#colorSpent)"
                    name="spent"
                  />
                  <Area 
                    yAxisId="right" 
                    type="monotone" 
                    dataKey="orders" 
                    stroke="#f59e0b" 
                    strokeWidth={2}
                    fill="url(#colorClientOrders)"
                    name="orders"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-72 flex items-center justify-center text-gray-500">
              <div className="text-center">
                <ChartBarIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p>No order data yet</p>
                <p className="text-sm text-gray-400 mt-1">Start ordering to see your trends</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Recent Orders & Favorites Row */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Recent Orders */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-6 border-b border-gray-100 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-gray-900">Recent Orders</h2>
              <p className="text-sm text-gray-500">Your latest purchases</p>
            </div>
            <Link to="/client/orders" className="text-amber-600 hover:text-amber-700 text-sm font-medium flex items-center">
              View All
              <ArrowRightIcon className="h-4 w-4 ml-1" />
            </Link>
          </div>
          
          {recentOrders.length === 0 ? (
            <div className="p-12 text-center text-gray-500">
              <ShoppingBagIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="font-medium">No orders yet</p>
              <p className="text-sm mt-1">Explore farms to start ordering</p>
              <Link to="/client/explore" className="inline-flex items-center mt-4 text-amber-600 hover:text-amber-700 text-sm font-medium">
                Explore Farms <ArrowRightIcon className="h-4 w-4 ml-1" />
              </Link>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {recentOrders.slice(0, 4).map(order => (
                <div key={order.id} className="p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <p className="font-medium text-gray-900">
                          #{order.orderNumber || order.id.slice(0, 8)}
                        </p>
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getStatusColor(order.status)}`}>
                          {order.status}
                        </span>
                      </div>
                      <p className="text-sm text-gray-500 mt-1">
                        {order.farm?.name || order.shopName || 'Farm'}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-gray-900">
                        KES {Number(order.totalAmount || 0).toLocaleString()}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {new Date(order.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Favorite Farms */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-6 border-b border-gray-100 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-gray-900">Favorite Farms</h2>
              <p className="text-sm text-gray-500">Farms you've saved</p>
            </div>
            <Link to="/client/explore" className="text-amber-600 hover:text-amber-700 text-sm font-medium flex items-center">
              Explore More
              <ArrowRightIcon className="h-4 w-4 ml-1" />
            </Link>
          </div>
          
          {favorites.length === 0 ? (
            <div className="p-12 text-center text-gray-500">
              <HeartIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="font-medium">No favorites yet</p>
              <p className="text-sm mt-1">Save farms you love for quick access</p>
              <Link to="/client/explore" className="inline-flex items-center mt-4 text-amber-600 hover:text-amber-700 text-sm font-medium">
                Find Farms <ArrowRightIcon className="h-4 w-4 ml-1" />
              </Link>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {favorites.slice(0, 4).map(fav => (
                <Link 
                  key={fav.id} 
                  to={`/shop/${fav.farm?.slug || fav.farmId}`}
                  className="p-4 hover:bg-gray-50 transition-colors flex items-center gap-4"
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-amber-100 to-orange-100 rounded-lg flex items-center justify-center overflow-hidden">
                    {fav.farm?.logoUrl ? (
                      <img src={fav.farm.logoUrl} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <BuildingStorefrontIcon className="h-6 w-6 text-amber-400" />
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{fav.farm?.name || 'Farm'}</p>
                    <p className="text-sm text-gray-500 flex items-center">
                      <MapPinIcon className="h-3 w-3 mr-1" />
                      {fav.farm?.county || 'Location'}
                    </p>
                  </div>
                  <HeartSolidIcon className="h-5 w-5 text-red-500" />
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Support Section */}
      <div className="bg-gradient-to-r from-gray-50 to-slate-50 border border-gray-200 rounded-xl p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h3 className="font-semibold text-gray-900 flex items-center">
              <ChatBubbleLeftRightIcon className="h-5 w-5 mr-2 text-gray-500" />
              Need Help?
            </h3>
            <p className="text-sm text-gray-600 mt-1">Our support team is here to assist you</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <a
              href="https://wa.me/254743673899"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
            >
              <PhoneIcon className="h-4 w-4 mr-2" />
              WhatsApp
            </a>
            <a
              href="mailto:omabraservices@gmail.com"
              className="inline-flex items-center px-4 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
            >
              <EnvelopeIcon className="h-4 w-4 mr-2" />
              Email Support
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientDashboard;
